import React, { useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Fab,
  TextField,
  InputAdornment,
  Chip,
  Card, CardContent,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Engineering as EngineeringIcon
} from "@mui/icons-material";
import { format, parseISO } from 'date-fns';

import ComponentForm from "./ComponentForm";
import { useComponents } from "../../../hooks/maintenance/useComponent";
import { useAuthContext } from "../../../context/AuthProvider";
import { useVehiculoDetalle } from "../../../hooks/useVehicles";

import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';

// ---- Toolbar compacto para DataGrid
function CompactToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarDensitySelector />
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
}

// ---- Helpers
const getChipColor = (status = '') => {
  switch (status.toUpperCase()) {
    case 'ACTIVO': return 'success';
    case 'MANTENIMIENTO': return 'warning';
    default: return 'default';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    return format(parseISO(dateString), 'dd/MM/yyyy');
  } catch {
    return dateString;
  }
};

const ComponentsByVehicleDashboard = () => {
  const { vehId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const [formState, setFormState] = useState({ open: false, component: null });
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, component: null });
  const [searchTerm, setSearchTerm] = useState("");

  // Grupos por sistema vehicular
  const { components: groups = [], isLoading, error, createMutation, updateMutation, deleteMutation } =
    useComponents(parseInt(vehId, 10));

  // Detalles del vehículo
  const { vehiculo: vehicleDetails, isLoading: isLoadingVehicle, error: errorVehicle } =
    useVehiculoDetalle(parseInt(vehId, 10));

  const showAlert = (message, severity = "success") => setAlert({ open: true, message, severity });
  const handleOpenForm = (component = null) => setFormState({ open: true, component });
  const handleCloseForm = () => setFormState({ open: false, component: null });

  // Submit crear/editar
  const handleSubmit = useCallback((data) => {
    if (!user?.EmpID) {
      showAlert("Debe iniciar sesión para realizar esta acción.", "error");
      return;
    }
    const payload = { ...data };
    if (formState.component) {
      payload.updbyid = user.EmpID;
      updateMutation.mutate(
        { id: formState.component.comid, data: payload },
        {
          onSuccess: () => { showAlert("Componente actualizado correctamente"); handleCloseForm(); },
          onError: (err) => { console.error(err); showAlert(err?.message || "Error al actualizar", "error"); },
        }
      );
    } else {
      payload.crebyid = user.EmpID;
      payload.vehid = parseInt(vehId, 10);
      createMutation.mutate(payload, {
        onSuccess: () => { showAlert("Componente creado correctamente"); handleCloseForm(); },
        onError: (err) => { console.error(err); showAlert(err?.message || "Error al crear", "error"); },
      });
    }
  }, [formState.component, createMutation, updateMutation, user, vehId]);

  // Confirmar eliminación lógica
  const handleConfirmDelete = () => {
    if (!user?.EmpID) {
      showAlert("Debe iniciar sesión para realizar esta acción.", "error");
      setConfirmDialog({ open: false, component: null });
      return;
    }
    deleteMutation.mutate(
      { id: confirmDialog.component.comid, updbyid: user.EmpID },
      {
        onSuccess: () => { showAlert("Componente eliminado lógicamente"); setConfirmDialog({ open: false, component: null }); },
        onError: (err) => { console.error(err); showAlert(err?.message || "Error al eliminar", "error"); },
      }
    );
  };

  // Filtrado por búsqueda (por grupo)
  const filteredGroups = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return groups;

    return groups.map(g => ({
      ...g,
      items: (g.items ?? []).filter(c =>
        (c.comnom ?? '').toLowerCase().includes(q) ||
        (c.tipocomponentenombre ?? '').toLowerCase().includes(q) ||
        (c.comdes ?? '').toLowerCase().includes(q)
      )
    })).filter(g => (g.items?.length ?? 0) > 0);
  }, [groups, searchTerm]);

  // Columnas para el DataGrid
  const columns = useMemo(() => ([
    { field: 'comid', headerName: 'ID', width: 80 },
    { field: 'tipocomponentenombre', headerName: 'Tipo', minWidth: 160, flex: 1 },
    { field: 'comnom', headerName: 'Nombre', minWidth: 180, flex: 1 },
    { field: 'comdes', headerName: 'Descripción', minWidth: 220, flex: 1 },
    {
      field: 'comest',
      headerName: 'Estado',
      width: 140,
      renderCell: (params) => (
        <Chip size="small" label={params.value} color={getChipColor(params.value)} />
      )
    },
    { field: 'comfecins', headerName: 'Fec. Instalación', width: 140, valueFormatter: ({ value }) => formatDate(value) },
    { field: 'comkmins', headerName: 'Km Instalación', width: 140, valueFormatter: ({ value }) => value ?? '—' },
    {
      field: 'actions',
      headerName: 'Acciones',
      sortable: false,
      filterable: false,
      width: 160,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => handleOpenForm(row)}>
                <EditIcon color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Ver Mantenimientos">
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigate(`/operaciones/componente/${row.comid}/mantenimiento`)}
              >
                <EngineeringIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar (lógica)">
              <IconButton size="small" onClick={() => setConfirmDialog({ open: true, component: row })}>
                <DeleteIcon color="error" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ]), [navigate]);

  // Loading / errores de vehículo
  if (isLoadingVehicle) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Cargando detalles del vehículo...</Typography>
      </Paper>
    );
  }
  if (errorVehicle) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography color="error" variant="h6">Error al cargar detalles del vehículo: {errorVehicle.message}</Typography>
        <Button onClick={() => navigate('/administracion/vehiculos')} startIcon={<ArrowBackIcon />}>Volver a Vehículos</Button>
      </Paper>
    );
  }
  if (!vehicleDetails) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography color="text.secondary" variant="h6">Vehículo no encontrado.</Typography>
        <Button onClick={() => navigate('/administracion/vehiculos')} startIcon={<ArrowBackIcon />}>Volver a Vehículos</Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/administracion/vehiculos')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">Componentes del Vehículo</Typography>
        </Box>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar componente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Info del vehículo */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Vehículo:&nbsp;
            <Typography component="span" variant="h6" color="text.primary">
              {vehicleDetails.vehmat} - {vehicleDetails.vehmar} {vehicleDetails.vehmod} ({vehicleDetails.vehani})
            </Typography>
          </Typography>
        </CardContent>
      </Card>

      {/* Grids por grupo */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
      ) : error ? (
        <Typography color="error">Error al cargar componentes: {error.message}</Typography>
      ) : filteredGroups.length === 0 ? (
        <Typography color="text.secondary">No hay componentes para este vehículo o no se encontraron resultados.</Typography>
      ) : (
        filteredGroups.map(group => {
          const rows = (group.items ?? []).map(r => ({ id: r.comid, ...r })); // id requerido por DataGrid
          return (
            <Paper key={`grp-${group.sisvehid}`} sx={{ p: 2, mb: 3, borderRadius: 2 }} elevation={1}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">
                  {group.sistemanombre}
                  <Chip size="small" label={rows.length} sx={{ ml: 1 }} />
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <div style={{ width: '100%' }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  autoHeight
                  disableRowSelectionOnClick
                  pageSizeOptions={[5, 10, 25]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10, page: 0 } },
                    columns: { columnVisibilityModel: { comdes: true } }
                  }}
                  slots={{ toolbar: CompactToolbar }}
                />
              </div>
            </Paper>
          );
        })
      )}

      {/* FAB crear */}
      <Fab color="primary" sx={{ position: "fixed", bottom: 20, right: 20 }} onClick={() => handleOpenForm()}>
        <AddIcon />
      </Fab>

      {/* Modal Crear/Editar */}
      {formState.open && (
        <ComponentForm
          open={formState.open}
          handleClose={handleCloseForm}
          onSubmit={handleSubmit}
          defaultValues={formState.component || { vehid: parseInt(vehId, 10), vehiculomatricula: vehicleDetails.vehmat }}
        />
      )}

      {/* Confirmación eliminar */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, component: null })}>
        <DialogTitle>Confirmar eliminación de componente</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar lógicamente el componente "{confirmDialog.component?.comnom}"?
            Esto lo marcará como inactivo en el sistema.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, component: null })} color="inherit">Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Alertas */}
      <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })}>
        <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, open: false })}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ComponentsByVehicleDashboard;
